import { ConfigApi, IdentityApi } from '@backstage/core-plugin-api';
import createDebugger from 'debug';
import { MarketplaceResponse } from '@principal-developer-portal/ui';
import { MarketplaceApi } from './marketplace-api';

const debug = createDebugger('marketplace:apiClient');

export class MarketplaceClient implements MarketplaceApi {
  private readonly configApi: ConfigApi;

  private readonly identityApi: IdentityApi;

  constructor(options: { configApi: ConfigApi; identityApi: IdentityApi }) {
    this.configApi = options.configApi;
    this.identityApi = options.identityApi;
  }

  private async getPatternsData(): Promise<MarketplaceResponse[]> {
    try {
      const { token } = await this.identityApi.getCredentials();

      const backendHost = this.configApi.getString('backend.baseUrl');

      const endpoint = `${backendHost}/api/marketplace/patterns`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      });

      const data: any = await response.json();

      if (data.statusCode !== 200) throw new Error(data.body.message);

      return JSON.parse(data.body) as MarketplaceResponse[];
    } catch (error: any) {
      debug(error);

      throw error;
    }
  }

  private async getUtilitiesData(): Promise<MarketplaceResponse[]> {
    try {
      const { token } = await this.identityApi.getCredentials();

      const backendHost = this.configApi.getString('backend.baseUrl');

      const endpoint = `${backendHost}/api/marketplace/utilities`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      });

      const data: any = await response.json();

      if (data.statusCode !== 200) throw new Error(data.body.message);

      return JSON.parse(data.body) as MarketplaceResponse[];
    } catch (error: any) {
      debug(error);

      throw error;
    }
  }

  private async getAllData(): Promise<MarketplaceResponse[]> {
    try {
      const { token } = await this.identityApi.getCredentials();

      const backendHost = this.configApi.getString('backend.baseUrl');

      const endpoint = `${backendHost}/api/marketplace/all`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      });

      const data: any = await response.json();

      if (data.statusCode !== 200) throw new Error(data.body.message);

      return JSON.parse(data.body) as MarketplaceResponse[];
    } catch (error: any) {
      debug(error);

      throw error;
    }
  }

  private async getApisData(): Promise<MarketplaceResponse[]> {
    try {
      const { token } = await this.identityApi.getCredentials();

      const backendHost = this.configApi.getString('backend.baseUrl');

      const endpoint = `${backendHost}/api/marketplace/apis`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      });

      const data: any = await response.json();

      if (data.statusCode !== 200) throw new Error(data.body.message);

      return JSON.parse(data.body) as MarketplaceResponse[];
    } catch (error: any) {
      debug(error);

      throw error;
    }
  }

  getPatterns(): Promise<MarketplaceResponse[]> {
    return this.getPatternsData();
  }

  getUtilities(): Promise<MarketplaceResponse[]> {
    return this.getUtilitiesData();
  }

  getAll(): Promise<MarketplaceResponse[]> {
    return this.getAllData();
  }

  getApis(): Promise<MarketplaceResponse[]> {
    return this.getApisData();
  }
}
